import { theme } from '@fable/theme'
import {
  StatsTheme,
  StatsThemeKey,
  StatsThemeMap,
  StatsThemeModel,
} from '../../types'

const colors = {
  pink: '#F3A6DD',
  blue: '#55ACDE',
  blueDark: '#00479B',
  green: '#8DC746',
  greenDark: '#315800',
  greenDark50: '#082D23',
  greenDark100: '#06120E',
  greenAlt: '#4F8111',
  greenDarkAlt: '#223D00',
  purple: '#4D0C98',
  purpleLight: '#9042E9',
  oxfordBlue: '#011F37',
  slateGray: '#637D95',
  richBlack: '#01040C',
}

const defaultColors = {
  model: 'core' as StatsThemeModel,
  bg100: colors.greenDark100,
  bg200: colors.greenDark50,
}

const pinkTheme: StatsTheme = {
  ...defaultColors,
  key: 'pink',
  isNew: false,
  primary: colors.pink,
  accent: colors.purple,
  summaryBg: colors.pink,
  summaryText: colors.purple,
  bg: colors.pink,
}

const blueTheme: StatsTheme = {
  ...defaultColors,
  key: 'blue',
  isNew: false,
  primary: colors.blue,
  accent: colors.blueDark,
  summaryBg: colors.blue,
  summaryText: colors.blueDark,
  bg: colors.blue,
}

const greenTheme: StatsTheme = {
  ...defaultColors,
  key: 'green',
  isNew: false,
  primary: colors.green,
  accent: colors.greenDark,
  summaryBg: colors.green,
  summaryText: colors.greenDark,
  bg: colors.green,
}

const spookyTheme: StatsTheme = {
  ...defaultColors,
  key: 'spooky',
  model: 'spooky',
  isNew: true,
  primary: colors.purpleLight,
  accent: colors.purple,
  summaryBg: 'transparent',
  summaryText: colors.purpleLight,
  bg: colors.greenDark100,
  bg100: colors.greenDark100,
  bg200: colors.greenDark100,
  insightBg: colors.greenDark50,
  streakCalendar: {
    bg: theme.colors.uiBlack40,
    daysReadBg: theme.colors.uiWhite50,
    tabBg: theme.colors.uiWhite20,
    legend: {
      noReadingBg: colors.greenDark50,
    },
  },
}

const holidayTheme: StatsTheme = {
  ...defaultColors,
  key: 'holiday',
  model: 'holiday',
  isNew: true,
  primary: theme.colors.uiTealLight,
  accent: theme.colors.uiTealDark,
  summaryBg: 'transparent',
  summaryText: theme.colors.uiTealLight,
  bg: colors.slateGray,
  bg100: colors.richBlack,
  bg200: colors.richBlack,
  insightBg: colors.oxfordBlue,
  streakCalendar: {
    bg: colors.oxfordBlue,
    daysReadBg: theme.colors.uiWhite20,
    tabBg: theme.colors.uiWhite20,
    legend: {
      noReadingBg: colors.oxfordBlue,
    },
  },
  monthlyBooks: {
    ghostBar: colors.oxfordBlue,
  },
}

export const statsColors = {
  base: colors,
  pink: pinkTheme,
  blue: blueTheme,
  green: greenTheme,
  spooky: spookyTheme,
  holiday: holidayTheme,
}

export const statsTheme: StatsThemeMap = {
  pink: statsColors.pink,
  blue: statsColors.blue,
  green: statsColors.green,
  spooky: statsColors.spooky,
  holiday: statsColors.holiday,
}

const ENABLED_STATS_THEMES: StatsThemeKey[] = ['pink', 'blue', 'green']

export const getEnabledStatsThemes = (): StatsThemeMap =>
  (Object.keys(statsTheme) as StatsThemeKey[])
    .filter((key) => ENABLED_STATS_THEMES.includes(key))
    .reduce((obj, key) => {
      obj[key] = statsTheme[key]
      return obj
    }, {} as StatsThemeMap)
